import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Button, createStyles, Group, Paper, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ProcedureChipPopoverContents from 'procedures/chip-utils/ProcedureChipPopoverContents';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {CenteredSpinner} from 'ui-library/Spinner';

import {ProcedureCardQuery, ProcedureCardQueryVariables} from './__generated/ProcedureCardQuery';
import EditProcedureModal, {ProcedureChangeParams} from './EditProcedureModal';

interface ProcedureCardProps {
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
  isEditable: boolean;
  hasUpdateAvailable: boolean;
  onOpenUpdateModal: () => void;
}

export const PROCEDURE_CARD_QUERY = gql`
  query ProcedureCardQuery($procedureId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        name
        procedureType
        templateCollection {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ProcedureCard({
  procedureId,
  flowchartVersionId,
  procedureType,
  studyType,
  onProcedureChange,
  isEditable,
  hasUpdateAvailable,
  onOpenUpdateModal,
}: ProcedureCardProps): JSX.Element | null {
  const result = useBenchlingQuery<ProcedureCardQuery, ProcedureCardQueryVariables>(PROCEDURE_CARD_QUERY, {
    variables: {procedureId},
  });
  const {classes} = useStyles();

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (
    result.type === QueryResultType.ERROR ||
    result.data.procedure == null ||
    isInaccessible(result.data.procedure)
  ) {
    return null;
  }

  const procedure = result.data.procedure;
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });

  return (
    <Stack spacing={12} {...getTestProps({component: 'ProcedureCard', element: 'root'})}>
      <Text className={classes.sectionTitle}>{`Active ${procedureString}`}</Text>
      <Paper withBorder={false} className={classes.card}>
        <div className={classes.cardContent}>
          <Group position="apart" align="center" noWrap={true}>
            <div className={classes.popoverWrapper}>
              <Popover
                renderContent={() => (
                  <ProcedureChipPopoverContents id={procedure.id} flowchartVersionId={flowchartVersionId} />
                )}
              >
                <div
                  className={classes.popoverTrigger}
                  {...getTestProps({component: 'ProcedureCard', element: 'popoverTrigger'})}
                >
                  <Stack spacing={2}>
                    <Group spacing={8} noWrap={true}>
                      {hasUpdateAvailable ? (
                        <BenchlingIcon
                          icon="exclamation"
                          color={Color.RED_500}
                          className={classes.alertIcon}
                        />
                      ) : (
                        <BenchlingIcon
                          icon={getProcedureIcon(procedure.procedureType)}
                          className={classes.icon}
                        />
                      )}
                      <Text className={classes.procedureName}>{procedure.name}</Text>
                    </Group>
                    <Text className={classes.collectionName}>{procedure.templateCollection.name}</Text>
                  </Stack>
                </div>
              </Popover>
            </div>
            {isEditable && (
              <UnstyledButton
                // TODO (BNCH-153428): Removing should not open this modal once procedure search in sidepanel is
                // available
                onClick={async () => {
                  await legacyRunModal(EditProcedureModal, {
                    procedureId,
                    flowchartVersionId,
                    procedureType,
                    studyType,
                    excludeLegacyProcedures: true,
                    onProcedureChange,
                    onRequestClose: () => {},
                  });
                }}
                className={classes.trashButton}
                aria-label={`Change ${procedureString}`}
                {...getTestProps({component: 'ProcedureCard', element: 'trashIconButton'})}
              >
                <BenchlingIcon icon="delete" className={classes.trashIcon} />
              </UnstyledButton>
            )}
          </Group>
        </div>
        {hasUpdateAvailable && (
          <div className={classes.updateButton}>
            <Button
              variant="ghost"
              compact={true}
              classNames={{root: classes.updateButtonRoot, label: classes.updateButtonLabel}}
              onClick={onOpenUpdateModal}
              {...getTestProps({component: 'ProcedureCard', element: 'updateButton'})}
            >
              Update available
            </Button>
          </div>
        )}
      </Paper>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  sectionTitle: {
    color: Color.BLACK,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '14px',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    borderColor: theme.colors.gray[3],
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
  },
  cardContent: {
    padding: 8,
  },
  icon: {
    color: theme.colors.gray[7],
    fontSize: 16,
    lineHeight: 1,
  },
  alertIcon: {
    fontSize: 16,
    lineHeight: 1,
  },
  procedureName: {
    color: Color.BLACK,
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    lineHeight: '16px',
  },
  collectionName: {
    color: theme.colors.gray[6],
    fontSize: 11,
    lineHeight: '14px',
  },
  popoverWrapper: {
    flexBasis: '100%',
    minWidth: 0,
  },
  popoverTrigger: {
    cursor: 'pointer',
    width: '100%',
  },
  trashButton: {
    color: theme.colors.gray[6],
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    flexShrink: 0,
  },
  trashIcon: {
    fontSize: 16,
    lineHeight: 1,
  },
  updateButton: {
    paddingTop: theme.spacing.xs,
    marginTop: theme.spacing.xs,
    paddingLeft: 8,
    paddingRight: 8,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
  updateButtonRoot: {
    paddingLeft: 0,
  },
  updateButtonLabel: {
    color: theme.colors.blue[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: '18px',
  },
}));
